/**
 * Etiquetas, tonos y formateos propios del módulo de Calibración.
 */

import type { BadgeVariant } from '@/components/ui/badge';
import { formatNumber, toIsoDate, toNumber } from '@/lib/utils';

import type { CodigoCalibracion, EstadoCodigo } from '../calibracion.types';

export const ESTADO_CODIGO_LABELS: Record<EstadoCodigo, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  expirado: 'Expirado',
  consumido: 'Consumido',
};

export const ESTADO_CODIGO_BADGES: Record<EstadoCodigo, BadgeVariant> = {
  activo: 'success',
  inactivo: 'neutral',
  expirado: 'warning',
  consumido: 'brand',
};

export const ESTADOS_CODIGO: readonly EstadoCodigo[] = [
  'activo',
  'expirado',
  'inactivo',
  'consumido',
];

export function estadoCodigoLabel(estado: EstadoCodigo | string | null | undefined): string {
  return ESTADO_CODIGO_LABELS[estado as EstadoCodigo] ?? String(estado ?? '—');
}

export function estadoCodigoBadge(estado: EstadoCodigo | string | null | undefined): BadgeVariant {
  return ESTADO_CODIGO_BADGES[estado as EstadoCodigo] ?? 'neutral';
}

/**
 * Texto de vigencia de un código.
 *
 * `dias_restantes` puede ser negativo (ya venció) y `null` cuando el código no
 * tiene fecha de expiración.
 */
export function vigenciaCodigo(codigo: Pick<CodigoCalibracion, 'dias_restantes' | 'estado_efectivo'>): string {
  const dias = codigo.dias_restantes;

  if (dias === null || dias === undefined) return 'Sin vencimiento';

  // Vale para cualquier estado: un código consumido o inactivo también pudo
  // haber caducado, y el número negativo se lee como "hace N días".
  if (dias < 0) {
    const pasado = Math.abs(dias);

    return `Venció hace ${pasado} ${pasado === 1 ? 'día' : 'días'}`;
  }

  if (dias === 0) return 'Vence hoy';
  if (dias === 1) return 'Vence mañana';

  return `Vence en ${dias} días`;
}

/** Duración legible de una calibración (se guarda en minutos). */
export function duracionTexto(minutos: number | null | undefined): string {
  const total = toNumber(minutos);

  if (!Number.isFinite(total) || total <= 0) return '—';
  if (total < 60) return `${formatNumber(total)} min`;

  const horas = Math.floor(total / 60);
  const resto = Math.round(total % 60);

  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

/** Nombre del ejecutor o guion. */
export function ejecutorTexto(nombre: string | null | undefined): string {
  return (nombre ?? '').trim() || 'Sin ejecutor';
}

/** Atajos de período del reporte. */
export const PRESETS_RANGO = [
  { id: 'mes', label: 'Este mes' },
  { id: 'mes-anterior', label: 'Mes anterior' },
  { id: '90d', label: 'Últimos 90 días' },
  { id: 'anio', label: 'Este año' },
] as const;

export type PresetRango = (typeof PRESETS_RANGO)[number]['id'];

/** Primer día del mes en curso y hoy (valores por defecto del panel anterior). */
export function rangoMesActual(): { fecha_inicio: string; fecha_fin: string } {
  const hoy = new Date();

  return {
    fecha_inicio: toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    fecha_fin: toIsoDate(hoy),
  };
}

/** Rango de fechas de cada atajo. */
export function rangoDePreset(preset: PresetRango): { fecha_inicio: string; fecha_fin: string } {
  const hoy = new Date();
  const fin = toIsoDate(hoy);

  switch (preset) {
    case 'mes':
      return rangoMesActual();
    case 'mes-anterior': {
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const ultimo = new Date(hoy.getFullYear(), hoy.getMonth(), 0);

      return { fecha_inicio: toIsoDate(inicio), fecha_fin: toIsoDate(ultimo) };
    }
    case '90d': {
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 90);

      return { fecha_inicio: toIsoDate(inicio), fecha_fin: fin };
    }
    default:
      return { fecha_inicio: toIsoDate(new Date(hoy.getFullYear(), 0, 1)), fecha_fin: fin };
  }
}
